import { SearchParams, SearchResponse } from 'typesense/lib/Typesense/Documents'
import { MultiSearchRequestSchema } from 'typesense/lib/Typesense/MultiSearch'

import {
  ETypesenseLotFacets,
  LotStatus,
  TypesenseLot,
  TypesenseLotFacets,
} from '@bonhams1793/bonhams-typescript'

import { LotIndices } from '@nx/global-types'

import { TypesenseClientInstance } from '../typesenseClient'
import {
  buildFilterString,
  extractDocuments,
  extractFacets,
  generateFacetsQueries,
} from '../typesenseHelpers'

export type TypesenseLotsResponse = Omit<
  SearchResponse<TypesenseLot>,
  'hits'
> & {
  facets: Record<string, Record<string, number>>
  hits: TypesenseLot[]
  nbPages: number
  nbHits: number
}

export type GetTypesenseLotsParams = SearchParams & {
  query?: string
  collection?: 'lots'
  auctionId?: string
  minPrice?: number
  maxPrice?: number
  minGBPPrice?: number
  maxGBPPrice?: number
  departmentCode?: string[]
  departments?: string[]
  chronology?: 'past' | 'future'
  isWithoutReserve?: boolean
  isToBeSold?: boolean
  isStarLot?: boolean
  excludeWithdrawn?: boolean
  excludeUnsold?: boolean
  country?: string[]
  brand?: string[] | null
  lotItemIds?: number[] | string[]
  groups?: string[]
  filters?: string
  allowedFacets?: ETypesenseLotFacets[]
}

const FACET_BY = Object.values(TypesenseLotFacets)

export function getTypesenseLots({
  typesenseRequest,
}: TypesenseClientInstance) {
  return async ({
    collection = 'lots',
    auctionId,
    minPrice,
    maxPrice,
    minGBPPrice,
    maxGBPPrice,
    departments = [],
    departmentCode = [],
    chronology,
    excludeWithdrawn = false,
    excludeUnsold = false,
    country = [],
    brand = [],
    lotItemIds = [],
    groups = [],
    isWithoutReserve,
    isToBeSold,
    isStarLot,
    filter_by = '',
    query_by = ['title'].join(','),
    sort_by = 'lotNo.number:asc,lotNo.full:asc,lotNo.letter:asc',
    page = 0,
    per_page = 24,
    query = '',
    allowedFacets,
    ...searchArgs
  }: GetTypesenseLotsParams): Promise<TypesenseLotsResponse> => {
    const facetFiltersRelation: Record<string, (string | number)[]> = {
      'country.name': [country].flat().filter(Boolean),
      'department.name': [departments].flat().filter(Boolean),
      'department.code': [departmentCode].flat().filter(Boolean),
      brand: [brand].flat().filter(Boolean),
      groups: [groups].flat().filter(Boolean),
    }

    if (chronology) {
      facetFiltersRelation['status'] =
        chronology === 'future'
          ? [LotStatus.new]
          : [`${LotStatus.sold}`, `${LotStatus.withoutResults}`]
    }

    const filters = [
      buildFilterString('auctionId', auctionId ? `${auctionId}` : ''),
      buildFilterString(
        'flags.isWithoutReserve',
        isWithoutReserve ? 'true' : ''
      ),
      buildFilterString(
        'status',
        excludeWithdrawn && !chronology
          ? {
              value: LotStatus.withdrawn,
              exclude: true,
            }
          : ''
      ),
      buildFilterString(
        'status',
        excludeUnsold && !chronology
          ? {
              value: LotStatus.unsold,
              exclude: true,
            }
          : ''
      ),
      buildFilterString('lotItemId', lotItemIds),
      filter_by,
      buildFilterString('flags.isStarLot', isStarLot ? 'true' : ''),
    ]

    if (typeof isToBeSold !== 'undefined') {
      filters.push(
        buildFilterString('flags.isToBeSold', isToBeSold ? 'true' : '')
      )
    }

    const facetFilters = Object.entries(facetFiltersRelation).map(
      ([key, value]) => buildFilterString(key, value)
    )

    if (typeof minPrice !== 'undefined' && typeof maxPrice !== 'undefined') {
      filters.push(
        `(price.estimateLow:>=${minPrice} && price.estimateHigh:<=${maxPrice})`
      )
    }

    if (
      typeof minGBPPrice !== 'undefined' &&
      typeof maxGBPPrice !== 'undefined'
    ) {
      filters.push(
        `(price.GBPLowEstimate:>=${minGBPPrice} && price.GBPHighEstimate:<=${maxGBPPrice})`
      )
    }

    const finalFilters = [...filters, ...facetFilters]
      .filter(Boolean)
      .join(' && ')

    const mainParams: MultiSearchRequestSchema = {
      exclude_fields: ['footnotes', 'catalogDesc'].join(','),
      filter_by: filters.filter(Boolean).join(' && '),
      facet_by: Array.isArray(allowedFacets)
        ? allowedFacets.join(',')
        : FACET_BY.join(','),
      query_by,
      sort_by,
      page: Math.max(1, page),
      per_page,
      max_facet_values: 300,
      q: query,
      ...searchArgs,
    }

    const facetQueries = generateFacetsQueries(
      facetFiltersRelation,
      mainParams,
      allowedFacets
    )

    // We want to disable TS cache when we're sorting by "Available to bid".
    const isCacheEnabled = !sort_by.includes('flags.isToBeSold:desc')
    const mainResponse = typesenseRequest<SearchResponse<TypesenseLot>>(
      collection,
      { ...mainParams, filter_by: finalFilters },
      isCacheEnabled
    )

    const facetQueryResponses = facetQueries.map((params) =>
      typesenseRequest<SearchResponse<TypesenseLot>>(
        collection,
        params,
        isCacheEnabled
      )
    )

    const [mainResult, ...extraResults] = await Promise.all([
      mainResponse,
      ...facetQueryResponses,
    ])

    const facetCounts = [
      ...(mainResult.facet_counts ?? []),
      ...extraResults.flatMap((extra) => extra.facet_counts),
    ].filter(Boolean)

    return {
      ...extractDocuments(mainResult),
      facets: extractFacets(facetCounts),
      facet_counts: facetCounts,
      nbPages: Math.ceil(mainResult.found / per_page) || 0,
      nbHits: mainResult.found,
    }
  }
}

export const mapIndexToSortBy = (
  searchIndex: LotIndices | undefined,
  includeRankingSort?: boolean
) => {
  const sortByMapping: Record<LotIndices, string> = {
    lots: includeRankingSort ? 'ranking:desc' : '',
    lots_virtual_sort_lotno_desc: includeRankingSort
      ? 'lotNo.number:desc,lotNo.full:desc,ranking:desc'
      : 'lotNo.number:desc,lotNo.full:desc',
    lots_virtual_sort_lotno_asc: includeRankingSort
      ? 'lotNo.number:asc,lotNo.full:asc,ranking:desc'
      : 'lotNo.number:asc,lotNo.full:asc',
    lots_virtual_sort_price_desc: includeRankingSort
      ? 'price.hammerPremium:desc,price.estimateLow:desc,ranking:desc'
      : 'price.hammerPremium:desc,price.estimateLow:desc',
    lots_virtual_sort_price_asc: includeRankingSort
      ? 'price.hammerPremium:asc,price.estimateLow:asc,ranking:desc'
      : 'price.hammerPremium:asc,price.estimateLow:asc',
    lots_virtual_sort_to_be_sold_desc:
      'flags.isToBeSold:desc,lotNo.number:asc,lotNo.full:asc',
    lots_virtual_sort_hammertime_desc: includeRankingSort
      ? 'hammerTime.timestamp:desc,ranking:desc'
      : 'hammerTime.timestamp:desc',
    lots_virtual_sort_hammertime_asc: includeRankingSort
      ? 'hammerTime.timestamp:asc,ranking:desc'
      : 'hammerTime.timestamp:asc',
    lots_virtual_sort_gbp_price_asc: includeRankingSort
      ? 'price.GBPLowEstimate:asc,ranking:desc'
      : 'price.GBPLowEstimate:asc',
    lots_virtual_sort_gbp_price_desc: includeRankingSort
      ? 'price.GBPLowEstimate:desc,ranking:desc'
      : 'price.GBPLowEstimate:desc',
  }

  return searchIndex
    ? sortByMapping[searchIndex]
    : sortByMapping['lots_virtual_sort_lotno_asc']
}
